import React, { useEffect, useState } from 'react';
import axios from 'axios';
import StatDateRange, { useRangeParams } from './StatDateRange';
import LeadsInsights from '../Leads/LeadsInsights';
import '../Leads/leads.css';
import './statCharts.css';

/**
 * Leads on the Statistics page (owner, 2026-09-09: "we had a leads statistics
 * on the leads page already — this can match that"). So it does, exactly:
 * the same six figures the Leads page puts at the top, in the same words,
 * and the same insight panels underneath — one component, one endpoint
 * (GET /api/leads/stats), so the two pages can never disagree. The only
 * difference is the date control: presets and a custom range here, kept in
 * the URL, with "All time" as the default like the Leads page.
 */
// Stable defaults: a new object per render would defeat the memo in useRangeParams.
const RANGE_DEFAULTS = { from: '', to: '' };

export default function LeadsStat() {
    const [range, setRange] = useRangeParams(RANGE_DEFAULTS);
    const [data, setData] = useState(null);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        axios.get('/api/leads/meta').then((r) => setMeta(r.data)).catch(() => { /* labels fall back to keys */ });
    }, []);

    useEffect(() => {
        let alive = true;
        setLoading(true); setError('');
        axios.get('/api/leads/stats', { params: { from: range.from || undefined, to: range.to || undefined } })
            .then((res) => { if (alive) setData(res.data); })
            .catch((e) => { if (alive) setError(e.response?.data?.error || 'Could not load lead statistics.'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [range]);

    const t = data?.totals || {};

    return (
        <div className="sc-view lds">
            <StatDateRange value={range} onChange={setRange} allTime loading={loading} />
            {loading && data && <div className="sc-updating" role="status">Updating the figures for this range…</div>}
            {error && <div className="sc-empty">{error}</div>}
            {!error && !data && <div className="sc-empty">Loading…</div>}
            {data && (
                <div className={loading ? 'sc-dim' : ''}>
                    {/* The Leads page's own tiles, verbatim. */}
                    <div className="lds__stats">
                        <div className="lds__stat">
                            <div className="lds__statLabel">Inquiries</div>
                            <div className="lds__statValue">{t.inquiries ?? '—'}</div>
                            <div className="lds__statNote">{t.people ?? 0} different people</div>
                        </div>
                        <div className="lds__stat">
                            <div className="lds__statLabel">Registered</div>
                            <div className="lds__statValue">{t.converted_people ?? '—'}</div>
                            <div className="lds__statNote">
                                {t.conversion_rate == null ? '—' : `${t.conversion_rate.toFixed(1)}% of people`}
                            </div>
                        </div>
                        <div className="lds__stat">
                            <div className="lds__statLabel">Made a booking</div>
                            <div className="lds__statValue">{t.booked_people ?? '—'}</div>
                            <div className="lds__statNote">
                                {t.registered ? `${t.registered} signed up - no bookings` : 'of those who registered'}
                            </div>
                        </div>
                        <div className="lds__stat">
                            <div className="lds__statLabel">Still open</div>
                            <div className="lds__statValue">{t.open ?? '—'}</div>
                            <div className="lds__statNote">not yet won or lost</div>
                        </div>
                        <div className={`lds__stat${t.due ? ' lds__stat--alert' : ''}`}>
                            <div className="lds__statLabel">Follow up today</div>
                            <div className="lds__statValue">{t.due ?? '—'}</div>
                            <div className="lds__statNote">due or overdue</div>
                        </div>
                        {t.avg_days_to_convert != null && t.converted_measured > 0 && (
                            <div className="lds__stat">
                                <div className="lds__statLabel">Time to convert</div>
                                <div className="lds__statValue">{Math.round(t.avg_days_to_convert)}</div>
                                <div className="lds__statNote">
                                    days, on average · {t.converted_measured} lead{t.converted_measured === 1 ? '' : 's'}
                                </div>
                            </div>
                        )}
                    </div>
                    <LeadsInsights stats={data} labels={meta?.labels} />
                </div>
            )}
        </div>
    );
}
